import { z } from "zod";
import { WEB_SEARCH_TOOL_NAME } from "./citations.ts";
import { PRODUCT_HELP_TOOL_NAME } from "./help.ts";
import { WEB_FETCH_TOOL_NAME } from "./web-fetch.ts";

/**
 * A feedback report's origin and the agent context of a report sent from a conversation
 * (openspec change `feedback-from-conversation`, design D4): a closed vocabulary only, so the
 * operators' read never carries text, a title or an identifier from the tenant.
 */

export const FEEDBACK_DRAFT_TOOL_NAME = "draft_feedback";

/** Where a report came from: the account menu, or a draft from a conversation. */
export const FEEDBACK_ORIGINS = [
  "menu",
  "agent_requested",
  "agent_suggested",
  "answer_action",
] as const;
export const feedbackOriginSchema = z.enum(FEEDBACK_ORIGINS);
export type FeedbackOrigin = z.infer<typeof feedbackOriginSchema>;

/**
 * The tool names an agent context may name (design D4): ESI's own tools. Any other tool (a
 * connector's, a tenant's) is reported as `other`, so a name a tenant chose never leaves it.
 */
export const AGENT_CONTEXT_TOOLS = [
  "search_knowledge",
  "document_outline",
  "open_section",
  "propose_wiki_page",
  "wiki_tree",
  "wiki_read",
  "wiki_write",
  "wiki_changes",
  "create_artefact",
  "update_artefact",
  "search_attachments",
  "attachment_outline",
  "open_attachment_section",
  PRODUCT_HELP_TOOL_NAME,
  WEB_SEARCH_TOOL_NAME,
  WEB_FETCH_TOOL_NAME,
  FEEDBACK_DRAFT_TOOL_NAME,
  "other",
] as const;
export const agentContextToolSchema = z.enum(AGENT_CONTEXT_TOOLS);
export type AgentContextTool = z.infer<typeof agentContextToolSchema>;

/**
 * Why a tool call failed, in a closed set (design D4): the governed tools' provider codes
 * (`throttled`, `unavailable`, `invalid`, `upstream`), `timeout`, a gate or provider refusal
 * (`blocked`, `refused`), the user's denial (`denied`), and `failed` / `error` when nothing
 * finer is known (an internal tool's error).
 */
export const AGENT_ERROR_CODES = [
  "timeout",
  "throttled",
  "unavailable",
  "invalid",
  "upstream",
  "refused",
  "blocked",
  "denied",
  "failed",
  "error",
] as const;
export const agentErrorCodeSchema = z.enum(AGENT_ERROR_CODES);
export type AgentErrorCode = z.infer<typeof agentErrorCodeSchema>;

/** How the turn ended. */
export const AGENT_OUTCOMES = ["completed", "failed", "stopped"] as const;
export type AgentOutcome = (typeof AGENT_OUTCOMES)[number];

/** The model roles an answer runs on (ADR 0017): never a model ID. */
export const AGENT_CONTEXT_ROLES = ["chat", "reasoning", "fast"] as const;

export const AGENT_CONTEXT_TOOLS_MAX = 20;
export const AGENT_CONTEXT_ERRORS_MAX = 10;

/** The server-built agent context of a draft and of the report sent from it (design D4). */
export const agentContextSchema = z.strictObject({
  /** The answer's resolved role; absent when not known (an older answer). */
  role: z.enum(AGENT_CONTEXT_ROLES).optional(),
  /** The tools called in the turn, deduplicated, in first-call order. */
  tools: z.array(agentContextToolSchema).max(AGENT_CONTEXT_TOOLS_MAX),
  /** One entry per failed call, in order. */
  errors: z
    .array(z.strictObject({ tool: agentContextToolSchema, code: agentErrorCodeSchema }))
    .max(AGENT_CONTEXT_ERRORS_MAX),
  outcome: z.enum(AGENT_OUTCOMES),
});
export type AgentContext = z.infer<typeof agentContextSchema>;
